import React from 'react';
import { WebsiteDesignProps } from './types';
import { Button } from "@/components/ui/button";
import { Activity, Zap, Timer, HeartPulse, Dumbbell, Move, TrendingUp, ChevronRight } from 'lucide-react';

const SportsScience: React.FC<WebsiteDesignProps> = ({ previewData }) => {
    const schoolName = previewData?.name || "Apex Performance Lab";
    
    return (
        <div className="min-h-screen bg-[#111] font-sans text-white selection:bg-[#ccff00] selection:text-black italic">
            {/* Ticker Tape */}
            <div className="bg-[#ccff00] text-black py-2 overflow-hidden whitespace-nowrap">
                <div className="animate-marquee font-black uppercase text-xs tracking-widest flex gap-8">
                     <span>Biomechanics Analysis</span> /// <span>VO2 Max Testing</span> /// <span>Elite Recovery</span> /// <span>Nutritional Optimization</span> /// <span>Mental Conditioning</span>
                </div>
            </div>

            {/* Navbar */}
            <nav className="relative z-50 px-8 py-6 flex justify-between items-center border-b border-white/10 bg-[#111]">
                <span className="font-black text-2xl uppercase tracking-tighter italic text-white flex items-center gap-2">
                    <Activity className="text-[#ccff00]" /> {schoolName}
                </span>
                
                <div className="hidden md:flex gap-10 font-black uppercase text-sm tracking-widest text-zinc-500">
                    <a href="#" className="hover:text-white transition-colors">Lab</a>
                    <a href="#" className="hover:text-white transition-colors">Data</a>
                    <a href="#" className="hover:text-white transition-colors">Recovery</a>
                    <a href="#" className="hover:text-white transition-colors">Team</a>
                </div>

                <Button className="rounded-none bg-white text-black hover:bg-[#ccff00] font-black uppercase tracking-widest text-xs h-10 px-6 skew-x-[-10deg]">
                    Start Training
                </Button>
            </nav>

            {/* Hero Section */}
            <header className="relative py-24 px-8 overflow-hidden min-h-[80vh] flex items-center">
                <div className="absolute inset-0 z-0">
                    <img 
                        src="https://images.unsplash.com/photo-1517836357463-d25dfeac3438?q=80&w=2070&auto=format&fit=crop" 
                        alt="Sprinter" 
                        className="w-full h-full object-cover opacity-30 grayscale contrast-125"
                    />
                    <div className="absolute inset-0 bg-gradient-to-r from-[#111] via-[#111]/80 to-transparent"></div>
                </div>

                <div className="relative z-10 max-w-4xl">
                    <div className="inline-flex items-center gap-2 text-[#ccff00] border border-[#ccff00] px-4 py-1 mb-8 skew-x-[-10deg]">
                        <Zap size={14} fill="currentColor" /> <span className="text-xs font-black uppercase tracking-widest">High Performance Zone</span>
                    </div>
                    
                    <h1 className="text-7xl md:text-9xl font-black uppercase leading-[0.85] tracking-tighter mb-8 text-transparent bg-clip-text bg-gradient-to-br from-white to-zinc-600">
                        Push <br/>
                        Limits.
                    </h1>
                    
                    <p className="text-xl text-zinc-400 font-medium not-italic max-w-lg mb-12 border-l-4 border-[#ccff00] pl-6">
                        Bridging the gap between human physiology and data science. For athletes who demand the winning edge.
                    </p>
                    
                    <div className="flex gap-4">
                        <Button size="lg" className="rounded-none bg-[#ccff00] text-black hover:bg-white font-black uppercase tracking-widest text-sm h-14 px-10 skew-x-[-10deg]">
                            Analyze Stats
                        </Button>
                        <Button size="lg" variant="outline" className="rounded-none border-zinc-700 text-white hover:border-white font-black uppercase tracking-widest text-sm h-14 px-10 skew-x-[-10deg] bg-transparent">
                            View Facilities
                        </Button>
                    </div>
                </div>
            </header>

            {/* Metrics Grid */}
            <section className="border-t border-white/10 bg-[#0a0a0a] py-24 px-8 not-italic">
                <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-12">
                     {[
                         { icon: HeartPulse, label: "Physiology", desc: "Monitor vitals in real-time." },
                         { icon: Move, label: "Kinematics", desc: "Motion capture analysis." },
                         { icon: Dumbbell, label: "Strength", desc: "Force plate diagnostics." },
                     ].map((item, i) => (
                         <div key={i} className="bg-[#1a1a1a] p-8 border border-white/5 hover:border-[#ccff00] transition-colors group">
                             <div className="flex justify-between items-start mb-6">
                                 <item.icon size={32} className="text-zinc-600 group-hover:text-[#ccff00] transition-colors" />
                                 <span className="text-2xl font-black text-[#222]">0{i+1}</span>
                             </div>
                             <h3 className="text-2xl font-black text-white uppercase italic mb-2">{item.label}</h3>
                             <p className="text-zinc-500 text-sm font-medium">{item.desc}</p>
                             <div className="mt-6 flex items-center gap-2 text-[#ccff00] text-xs font-bold uppercase tracking-widest opacity-0 group-hover:opacity-100 transition-opacity">
                                 Read More <ChevronRight size={14} />
                             </div>
                         </div>
                     ))}
                </div>
            </section>

             {/* Footer */}
            <footer className="bg-[#111] text-zinc-600 py-20 px-8 border-t border-white/10">
                 <div className="max-w-7xl mx-auto flex flex-col items-center text-center">
                     <span className="font-black text-4xl text-white uppercase italic tracking-tighter mb-4">{schoolName}</span>
                     <div className="flex gap-8 font-black uppercase tracking-widest text-xs mb-12">
                         <a href="#" className="hover:text-[#ccff00]">Instagram</a>
                         <a href="#" className="hover:text-[#ccff00]">Strava</a>
                         <a href="#" className="hover:text-[#ccff00]">Youtube</a>
                     </div>
                     <p className="font-medium text-[10px] uppercase tracking-widest border border-zinc-800 px-4 py-2 skew-x-[-10deg]">
                         © 2026. Performance is Data.
                     </p>
                 </div>
            </footer>
        </div>
    );
};

export default SportsScience;
